import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../utils/apiConfig";
import { usePolarisRecaptcha } from "../hooks/usePolarisRecaptcha";
import { RecaptchaDisclosure } from "./RecaptchaDisclosure";
import polarisLogo from "../images/Polaris Logo.svg";
import "../styles/login-page.css";

export function ForgotPasswordPage() {
    const navigate = useNavigate();
    const { executeRecaptcha } = usePolarisRecaptcha();
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    async function handleSubmit(ev: React.FormEvent) {
        ev.preventDefault();
        setError("");
        setSuccessMessage("");

        if (!executeRecaptcha) {
            setError("Security verification is still loading. Please try again in a moment.");
            return;
        }

        setIsSubmitting(true);
        try {
            const recaptchaToken = await executeRecaptcha("forgot_password");
            const response = await fetch(buildApiUrl("api/auth/forgot-password"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, recaptchaToken }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const detail = String(data.detail ?? data.message ?? "");
                if (response.status === 400 && /recaptcha/i.test(detail)) {
                    setError("Security verification did not pass. Refresh the page and try again.");
                    return;
                }
                setError("Unable to process your request. Please try again.");
                return;
            }

            setSuccessMessage(
                data.message ?? "If an account exists for that email, we sent a password reset link."
            );
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <main className="login-page" style={{ backgroundColor: "#000000" }} data-testid="forgot-password-page">
            <div className="login-overlay" style={{ backgroundColor: "#00000066" }} />

            <section className="login-card" role="dialog" aria-label="Forgot password">
                <header className="login-head">
                    <img src={polarisLogo} alt="Polaris Athlete" className="login-logo" />
                    <p className="welcome">
                        Enter your email address and we&apos;ll send a secure one-time link
                        to reset your password.
                    </p>
                </header>

                <form onSubmit={handleSubmit} className="login-form">
                    {error && <div className="form-alert is-error" role="alert">{error}</div>}
                    {successMessage && (
                        <div className="form-alert is-success" role="status">{successMessage}</div>
                    )}

                    <div className="field">
                        <label htmlFor="forgot-email">Email Address</label>
                        <input
                            id="forgot-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="name@example.com"
                            required
                            autoComplete="username"
                            className="input"
                            autoFocus
                        />
                    </div>

                    <button
                        type="submit"
                        className="login-btn-primary"
                        disabled={isSubmitting || !email}
                    >
                        {isSubmitting ? "Sending link…" : "Send reset link"}
                    </button>

                    <button
                        type="button"
                        className="mfa-back-link"
                        onClick={() => navigate("/login")}
                    >
                        Back to login
                    </button>

                    <RecaptchaDisclosure className="recaptcha-disclosure--on-dark" />
                </form>
            </section>
        </main>
    );
}

export default ForgotPasswordPage;
